/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { CampaignOffer, EmailBlock, EmailTheme } from "../types";
import { STUB_PRODUCTS } from "../stub";
import { formatMoney, formatShortDate, usePreview } from "../shared";
import { formatEmailText } from "../richText";
import { sampleCouponCode } from "../variables";
import { CouponCard } from "../CouponCard";
import { FONT_STACKS } from "./blocks";

type EmailBlockViewProps = {
  block: EmailBlock;
  theme: EmailTheme;
  offer: CampaignOffer;
  mobile: boolean;
};

// Renders one block the way it would look in the inbox (inline styles only,
// mirroring what an email renderer would emit).
export const EmailBlockView = defineComponent<EmailBlockViewProps>({
  name: "EmailBlockView",
  props: ["block", "theme", "offer", "mobile"],
  setup(props) {
    const { t } = useLocalization();
    const preview = usePreview();

    return () => {
      const { block, theme } = props;
      const r = preview.render;
      const pad = props.mobile ? "16px 20px" : "16px 40px";

      switch (block.type) {
        case "heading":
          return (
            <h1
              style={{
                margin: 0,
                padding: pad,
                fontSize: block.level === 1 ? (props.mobile ? "26px" : "32px") : "22px",
                lineHeight: 1.2,
                fontWeight: 800,
                textAlign: block.align,
                color: theme.text_color,
                fontFamily: FONT_STACKS[theme.font],
              }}
            >
              {r(block.text)}
            </h1>
          );
        case "text":
          return (
            <div
              style={{ padding: pad, fontSize: "16px", lineHeight: 1.6, textAlign: block.align, color: theme.text_color }}
              innerHTML={formatEmailText(r(block.text), theme.brand_color)}
            />
          );
        case "image":
          return (
            <div style={{ padding: block.width === 100 ? "0" : pad, textAlign: "center" }}>
              <img
                src={block.src}
                alt={block.alt}
                style={{
                  width: `${block.width}%`,
                  display: "inline-block",
                  borderRadius: block.width === 100 ? 0 : `${theme.radius}px`,
                }}
              />
            </div>
          );
        case "button":
          return (
            <div style={{ padding: pad, textAlign: block.align }}>
              <span
                style={{
                  display: block.full_width ? "block" : "inline-block",
                  background: theme.brand_color,
                  color: "#fff",
                  padding: "14px 28px",
                  borderRadius: `${theme.radius}px`,
                  fontWeight: 700,
                  fontSize: "16px",
                  textAlign: "center",
                }}
              >
                {r(block.label)}
              </span>
            </div>
          );
        case "coupon": {
          const expiry =
            props.offer.type === "coupon" && props.offer.expires_in_days
              ? formatShortDate(new Date(Date.now() + props.offer.expires_in_days * 86_400_000))
              : "";
          return (
            <div style={{ padding: pad }}>
              <CouponCard
                offer={props.offer}
                code={preview.showRaw.value ? "{{coupon_code}}" : sampleCouponCode(preview.customer.value.id)}
                expiry={expiry}
                headline={r(block.headline)}
                showQr={block.show_qr}
                showExpiry={block.show_expiry}
                accent={theme.brand_color}
                textColor={theme.text_color}
                radius={theme.radius}
              />
            </div>
          );
        }
        case "products": {
          const products = block.product_ids
            .map((id) => STUB_PRODUCTS.find((p) => p.id === id))
            .filter((p) => !!p);
          const cols = props.mobile ? 2 : block.columns;
          return (
            <div
              style={{
                padding: pad,
                display: "grid",
                gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                gap: "12px",
              }}
            >
              {products.map((p) => (
                <div key={p!.id} style={{ textAlign: "center", color: theme.text_color }}>
                  <img
                    src={p!.image}
                    alt={p!.name}
                    style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: `${theme.radius}px` }}
                  />
                  <div style={{ fontWeight: 600, fontSize: "14px", marginTop: "6px" }}>{p!.name}</div>
                  <div style={{ fontSize: "13px", color: theme.brand_color }}>{formatMoney(p!.price)}</div>
                </div>
              ))}
            </div>
          );
        }
        case "divider":
          return (
            <div style={{ padding: pad }}>
              <div style={{ borderTop: `1px solid ${theme.text_color}`, opacity: 0.12 }} />
            </div>
          );
        case "spacer":
          return <div style={{ height: `${block.height}px` }} />;
        case "social": {
          const links = [
            block.instagram && "IG",
            block.facebook && "FB",
            block.website && "WWW",
          ].filter(Boolean) as string[];
          return (
            <div style={{ padding: pad, display: "flex", justifyContent: "center", gap: "10px" }}>
              {links.map((l) => (
                <span
                  key={l}
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "999px",
                    background: theme.brand_color,
                    color: "#fff",
                    fontSize: "11px",
                    fontWeight: 700,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {l}
                </span>
              ))}
            </div>
          );
        }
        case "footer":
          return (
            <div
              style={{ padding: pad, fontSize: "12px", lineHeight: 1.6, textAlign: "center", color: theme.text_color, opacity: 0.6 }}
            >
              <div>{r(block.text)}</div>
            </div>
          );
      }
    };
  },
});

// Compliance footer appended to every email; not removable by the merchant.
export const EmailLegalFooter = defineComponent<{ theme: EmailTheme }>({
  name: "EmailLegalFooter",
  props: ["theme"],
  setup(props) {
    const { t } = useLocalization();
    return () => (
      <div
        style={{ padding: "12px 24px 20px", fontSize: "11px", textAlign: "center", color: props.theme.text_color, opacity: 0.5 }}
      >
        <span style={{ textDecoration: "underline" }}>{t("campaigns.studio_email.unsubscribe")}</span>
        {" · "}
        <span style={{ textDecoration: "underline" }}>{t("campaigns.studio_email.view_in_browser")}</span>
      </div>
    );
  },
});
